import {
  Dialog,
  DialogProps,
  Fade,
  IconButton,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import React, { FC, ReactElement } from 'react';
import TypographyWrapper from '../TypographyWrapper/TypographyWrapper';
import { useDefaultShopStyles } from '../../hooks/useDefaultShopStyles';

const useStyles = makeStyles({
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: 'absolute',
    right: '0',
    top: '0',
    color: 'var(--shopColor60)',
    padding: '16px',
  },
  container: {
    backgroundColor: 'var(--popoverBackgroundColor)',
  },
});

// Wrapper class for the transition that removes any animation
const NoTransition = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof Fade>>(
  ({ children }, ref) =>
    React.cloneElement(children as ReactElement, {
      ref,
      tabIndex: -1,
    })
);

interface DialogWithCloseButtonProps extends DialogProps {
  allowFullScreen?: boolean;
  titleText?: string;
  onClose?: () => void;
  headerClassName?: string;
  closeButtonClassName?: string;
}

const DialogWithCloseButton: FC<DialogWithCloseButtonProps> = ({
  allowFullScreen = true,
  children,
  titleText,
  onClose,
  open,
  headerClassName,
  closeButtonClassName,
  ...rest
}) => {
  const classes = useStyles();

  const theme = useTheme();
  const modalFullScreen = allowFullScreen && useMediaQuery(theme.breakpoints.down('sm'));
  const { popoverBackgroundColor } = useDefaultShopStyles();

  return open ? (
    <Dialog
      fullScreen={modalFullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
      BackdropProps={{ style: { backdropFilter: 'blur(1px)' } }}
      TransitionComponent={NoTransition}
      {...rest}
    >
      <MuiDialogTitle
        disableTypography
        className={headerClassName || classes.root}
        style={{ backgroundColor: popoverBackgroundColor }}
      >
        {titleText && (
          // Right margin is to avoid overlapping the X button
          <h2 style={{ margin: 0, marginRight: '48px' }}>
            <TypographyWrapper variant="h2">{titleText}</TypographyWrapper>
          </h2>
        )}

        {onClose ? (
          <IconButton
            aria-label="close"
            className={closeButtonClassName || classes.closeButton}
            onClick={onClose}
            style={{ zIndex: 2 }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
      {children}
    </Dialog>
  ) : null;
};

export default DialogWithCloseButton;
